/**
 * The expenses screen's slice of the API. Same shape as `../api.ts`.
 */

import type { DextDelivery, ExpenseStatus } from "@baros/core";
import { ApiError, request as shared } from "../api.js";


/** A stored `EXPENSE#` item, as the list route returns it. */
export interface Expense {
  readonly expenseId: string;
  readonly at: string;
  readonly ref: string;
  readonly supplierId: string | null;
  readonly supplierName: string;
  readonly amountIncGst: number;
  readonly gst: number;
  readonly status: ExpenseStatus;
  readonly slipS3Key: string | null;
  /** The order this invoice pays for, once linked. */
  readonly orderId?: string | null;
  /** How offering the slip to Dext went, once it has been tried. */
  readonly dext?: DextDelivery | null;
  readonly note: string;
  readonly byUserId: string;
  readonly updatedAt?: string;
}

export interface ExpenseTotalsResponse {
  readonly count: number;
  readonly amountIncGst: number;
  readonly gst: number;
  readonly unmatched: number;
  readonly paid: number;
}

export interface ExpenseList {
  readonly expenses: readonly Expense[];
  readonly totals: ExpenseTotalsResponse;
}

/** What the form sends. `gst` omitted means "work out the eleventh". */
export interface ExpenseDraft {
  readonly ref: string;
  readonly supplierId: string | null;
  readonly supplierName: string;
  readonly amountIncGst: number;
  readonly gst?: number;
  /** The uploaded slip's key; omit to leave an existing one alone. */
  readonly slipS3Key?: string | null;
  readonly status: ExpenseStatus;
  readonly note: string;
  readonly at?: string | null;
}

async function request<T>(
  token: string,
  method: "GET" | "POST" | "PUT",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

const venuePath = (venueId: string) => `/v1/venues/${encodeURIComponent(venueId)}/expenses`;

/** What Textract read off a slip. Every field may be null; the person checks. */
export interface SlipReading {
  readonly supplierName: string | null;
  readonly total: number | null;
  readonly gst: number | null;
  readonly ref: string | null;
  readonly date: string | null;
  readonly lines: readonly { readonly name: string; readonly amount: number | null }[];
  readonly confidence: number | null;
}

/** One order the slip might be paying for, ranked, with the line-by-line comparison. */
export interface OrderCandidate {
  readonly orderId: string;
  readonly score: number;
  readonly reasons: readonly string[];
  readonly supplierId: string;
  readonly supplierName: string;
  readonly status: string;
  readonly subtotalIncGstCents: number;
  readonly sentAt: string | null;
  readonly reconciliation: {
    readonly matched: readonly {
      readonly order: { readonly name: string; readonly qty: number; readonly unitCostCents: number };
      readonly slip: { readonly name: string; readonly amount: number | null };
      readonly orderedAmount: number;
      readonly invoicedAmount: number | null;
      readonly delta: number | null;
    }[];
    readonly unbilled: readonly { readonly name: string; readonly qty: number; readonly unitCostCents: number }[];
    readonly unexpected: readonly { readonly name: string; readonly amount: number | null }[];
    readonly deltaTotal: number;
  };
}

const slipsPath = (venueId: string) => `/v1/venues/${encodeURIComponent(venueId)}/slips`;

export const slipsApi = {
  /** A presigned PUT for a new slip; upload the file to `url`, then keep `key`. */
  presign: (token: string, venueId: string, contentType: string) =>
    request<{ key: string; url: string; expiresIn: number }>(token, "POST", slipsPath(venueId), { contentType }),

  /** Upload the file itself, straight to the bucket. */
  upload: async (url: string, file: File): Promise<void> => {
    const res = await fetch(url, { method: "PUT", headers: { "content-type": file.type }, body: file });
    if (!res.ok) throw new ApiError(res.status, `The upload failed (${res.status})`);
  },

  read: (token: string, venueId: string, key: string) =>
    request<{ reading: SlipReading; key: string; matches: OrderCandidate[] }>(token, "POST", `${slipsPath(venueId)}/ocr`, { key }),

  /** Re-rank orders against a reading the person has corrected. */
  match: (token: string, venueId: string, reading: SlipReading) =>
    request<{ matches: OrderCandidate[] }>(token, "POST", `${slipsPath(venueId)}/match`, { reading }),

  /** Attach an expense to the order it pays for; the order becomes `received`. */
  link: (token: string, venueId: string, expenseId: string, orderId: string) =>
    request<{ expense: Expense; orderId: string }>(
      token,
      "POST",
      `/v1/venues/${encodeURIComponent(venueId)}/expenses/${encodeURIComponent(expenseId)}/link`,
      { orderId },
    ),

  viewUrl: (token: string, venueId: string, key: string) =>
    request<{ url: string }>(token, "GET", `${slipsPath(venueId)}/url?key=${encodeURIComponent(key)}`),
};

export const expensesApi = {
  /**
   * Everything recent, newest first. The route takes `from`/`to`; the screen
   * reads the lot and picks a month in the browser so the period stepper knows
   * which months have anything in them.
   */
  list: (token: string, venueId: string) => request<ExpenseList>(token, "GET", venuePath(venueId)),
  create: (token: string, venueId: string, body: ExpenseDraft) =>
    request<{ expense: Expense }>(token, "POST", venuePath(venueId), body),
  update: (token: string, venueId: string, expenseId: string, body: ExpenseDraft) =>
    request<{ expense: Expense }>(
      token,
      "PUT",
      `${venuePath(venueId)}/${encodeURIComponent(expenseId)}`,
      body,
    ),
  setStatus: (token: string, venueId: string, expenseId: string, status: ExpenseStatus) =>
    request<{ expense: Expense }>(
      token,
      "PUT",
      `${venuePath(venueId)}/${encodeURIComponent(expenseId)}/status`,
      { status },
    ),

  /**
   * Whether the organisation sends slips to Dext, for anyone who manages
   * expenses — the settings themselves need `org:manage` and live on screen 12.
   */
  dextConfig: (token: string, venueId: string) =>
    request<{ configured: boolean; autoSend: boolean }>(
      token,
      "GET",
      `/v1/venues/${encodeURIComponent(venueId)}/dext`,
    ),

  /**
   * Email the expense's slip to Dext. A 409 says why it cannot go — no slip,
   * a file Dext will not read, or already sent without `force`; a 502 is the
   * send itself failing, and the expense then carries the failure.
   */
  sendToDext: (token: string, venueId: string, expenseId: string, force: boolean) =>
    request<{ expense: Expense }>(
      token,
      "POST",
      `${venuePath(venueId)}/${encodeURIComponent(expenseId)}/dext`,
      force ? { force: true } : {},
    ),
};
